import { useState } from 'react'
import { useGame } from '@/context/GameContext'
import { getToken } from '@/api/client'
import QuestionEditor from './QuestionEditor'
import RosterManager from './RosterManager'
import MasteryReport from './MasteryReport'
import EngagementReport from './EngagementReport'
import AccessibilityPanel from '../AccessibilityPanel'
import ChangePasswordModal from './ChangePasswordModal'

type Tab = 'roster' | 'questions' | 'report' | 'growth' | 'engagement'

export default function TeacherDashboard() {
  const { teacher, logout, textScale } = useGame()
  const [tab, setTab] = useState<Tab>('roster')
  const [showA11y, setShowA11y] = useState(false)
  const [showPwd, setShowPwd]   = useState(false)
  const [backingUp, setBackingUp] = useState(false)

  // Download a consistent DB snapshot — the weekly safety net for student data.
  async function downloadBackup() {
    if (backingUp) return
    setBackingUp(true)
    try {
      const base = (import.meta.env.VITE_API_URL ?? (import.meta.env.PROD ? '' : 'http://localhost:3001')) as string
      const res = await fetch(`${base}/api/admin/backup`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error(`Backup failed (HTTP ${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `supercivicaworld-backup-${new Date().toISOString().slice(0, 10)}.db`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Backup failed')
    } finally {
      setBackingUp(false)
    }
  }

  const tabs: Array<{ key: Tab; label: string; icon: string }> = [
    { key: 'roster',    label: 'Classes & Roster', icon: '👥' },
    { key: 'questions', label: 'Question Authoring', icon: '✏️' },
    { key: 'report',    label: 'Mastery Report',   icon: '📊' },
    { key: 'growth',    label: 'Pre/Post Growth',   icon: '📈' },
    { key: 'engagement', label: 'Engagement', icon: '🔁' },
  ]

  return (
    // Fixed, self-scrolling shell so the dashboard escapes the global
    // `overflow: hidden` lock used by the full-screen student game.
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto',
                  background: '#0a0a1a', color: '#e0e7ff' }}>
    <div className="flex flex-col" style={{ minHeight: '100%', zoom: textScale }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-6 py-3"
              style={{ background: '#0f0f23', borderBottom: '2px solid #1e1b4b' }}>
        <div className="flex items-center gap-4">
          <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '12px', color: '#a78bfa' }}>
            ⚔️ SUPER CIVICA WORLD
          </span>
          <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6366f1' }}>
            Teacher Portal
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span style={{ fontFamily: 'system-ui', fontSize: '13px', color: '#94a3b8' }}>
            {teacher?.name}
          </span>
          <button onClick={downloadBackup} disabled={backingUp} title="Download a backup of all class data"
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                     background: '#1e1b4b', border: '1px solid #312e81',
                     color: backingUp ? '#4b5563' : '#818cf8', padding: '5px 10px',
                     borderRadius: 4, cursor: backingUp ? 'wait' : 'pointer' }}>
            {backingUp ? 'BACKING UP…' : '💾 BACKUP'}
          </button>
          <button onClick={() => setShowPwd(true)} title="Change your password"
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                     background: '#1e1b4b', border: '1px solid #312e81',
                     color: '#818cf8', padding: '5px 10px', borderRadius: 4, cursor: 'pointer' }}>
            🔑 PASSWORD
          </button>
          <button onClick={() => setShowA11y(true)} aria-label="Accessibility settings"
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '10px',
                     background: '#1e1b4b', border: '1px solid #312e81',
                     color: '#a78bfa', padding: '5px 10px', borderRadius: 4, cursor: 'pointer' }}>
            ♿
          </button>
          <button onClick={logout}
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                     background: '#1e1b4b', border: '1px solid #312e81',
                     color: '#818cf8', padding: '5px 10px', borderRadius: 4, cursor: 'pointer' }}>
            LOGOUT
          </button>
        </div>
      </header>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div className="flex border-b" style={{ borderColor: '#1e1b4b', background: '#0d0d23' }}>
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            className="px-5 py-3 transition-colors"
            style={{
              fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
              color: tab === t.key ? '#a78bfa' : '#4b5563',
              background: tab === t.key ? '#1e1b4b' : 'transparent',
              borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              cursor: 'pointer', border: 'none',
              borderBottomStyle: 'solid',
              borderBottomWidth: 2,
              borderBottomColor: tab === t.key ? '#6366f1' : 'transparent',
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <main className="flex-1 p-6">
        {tab === 'roster'    && <RosterManager />}
        {tab === 'questions' && <QuestionEditor />}
        {tab === 'report'    && <MasteryReport mode="mastery" />}
        {tab === 'growth'    && <MasteryReport mode="growth" />}
        {tab === 'engagement' && <EngagementReport />}
      </main>

      {showA11y && <AccessibilityPanel scaleText={false} onClose={() => setShowA11y(false)} />}
      {showPwd && teacher?.email && <ChangePasswordModal email={teacher.email} onClose={() => setShowPwd(false)} />}
    </div>
    </div>
  )
}
